import { getTenantClient } from '@sazeo/supabase-tenant';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SupabaseClientLike } from '../people';
import type { CreateJobPostingInput, JobPosting, UpdateJobPostingInput } from '../recruitment';

export interface VacancyDocument {
  id?: number;
  vacancy_id: string;
  tenant_id: string;
  document_key: string;
  is_required: boolean;
}

export interface VacancyTest {
  id?: number;
  vacancy_id: string;
  tenant_id: string;
  test_key: string;
  display_order: number;
  candidate_can_take_now: boolean;
}

export interface PublicVacancy {
  id: string;
  uid: string;
  title: string;
  status: string;
  employment_type: string | null;
  experience_level: string | null;
  work_arrangement: string | null;
  city: string | null;
  country: string;
  salary_min: number | null;
  salary_max: number | null;
  salary_currency: string;
  salary_negotiable: boolean;
  fields_data: Record<string, unknown>;
  vacancy_language: string;
  application_limit: number | null;
  expires_at: string | null;
  published_at: string | null;
  company: {
    name: string;
    logo_storage_path: string | null;
    brand_color: string;
    industry: string | null;
    company_size: string | null;
    website: string | null;
    description: string | null;
  };
  documents: VacancyDocument[];
  tests: VacancyTest[];
}

export interface PublicApplicationInput {
  vacancyId: string;
  fullName: string;
  email: string;
  phone?: string | null;
  linkedinUrl?: string | null;
  consent: boolean;
}

export interface PublicApplicationResult {
  application_id: string;
  public_token: string;
  vacancy_id: string;
  tenant_id: string;
}

function db(client?: SupabaseClientLike | null): SupabaseClientLike {
  return client ?? (getTenantClient() as unknown as SupabaseClientLike);
}

async function currentTenantId(client: SupabaseClientLike): Promise<string> {
  if (!client.rpc) throw new Error('Supabase RPC client is required');
  const { data, error } = await client.rpc('current_tenant_id');
  if (error) throw new Error(error.message ?? 'Unable to resolve tenant');
  if (!data) throw new Error('Active tenant is required');
  return String(data);
}

export const vacancyBuilderKeys = {
  all: ['hr-vacancy-builder'] as const,
  documents: (vacancyId: string) => [...vacancyBuilderKeys.all, 'documents', vacancyId] as const,
  tests: (vacancyId: string) => [...vacancyBuilderKeys.all, 'tests', vacancyId] as const,
  publicVacancy: (uid: string) => [...vacancyBuilderKeys.all, 'public', uid] as const,
  posting: (id: string) => [...vacancyBuilderKeys.all, 'posting', id] as const,
};

export function useVacancyDocuments(vacancyId: string, client?: SupabaseClientLike | null) {
  return useQuery({
    queryKey: vacancyBuilderKeys.documents(vacancyId),
    enabled: Boolean(vacancyId),
    queryFn: async (): Promise<VacancyDocument[]> => {
      const { data, error } = await db(client)
        .from('vacancy_documents')
        .select('*')
        .eq('vacancy_id', vacancyId)
        .order('document_key');
      if (error) throw new Error(error.message ?? 'Unable to load vacancy documents');
      return (data ?? []) as VacancyDocument[];
    },
  });
}

export function useVacancyTests(vacancyId: string, client?: SupabaseClientLike | null) {
  return useQuery({
    queryKey: vacancyBuilderKeys.tests(vacancyId),
    enabled: Boolean(vacancyId),
    queryFn: async (): Promise<VacancyTest[]> => {
      const { data, error } = await db(client)
        .from('vacancy_tests')
        .select('*')
        .eq('vacancy_id', vacancyId)
        .order('display_order');
      if (error) throw new Error(error.message ?? 'Unable to load vacancy tests');
      return (data ?? []) as VacancyTest[];
    },
  });
}

export function useSetVacancyDocuments(client?: SupabaseClientLike | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      docs,
      tenantId,
      vacancyId,
    }: {
      vacancyId: string;
      tenantId: string;
      docs: Pick<VacancyDocument, 'document_key' | 'is_required'>[];
    }): Promise<void> => {
      const supabase = db(client);
      const { error: deleteError } = await supabase
        .from('vacancy_documents')
        .delete()
        .eq('vacancy_id', vacancyId);
      if (deleteError) throw new Error(deleteError.message ?? 'Unable to reset documents');
      if (docs.length === 0) return;
      const { error } = await supabase.from('vacancy_documents').insert(
        docs.map((doc) => ({
          ...doc,
          tenant_id: tenantId,
          vacancy_id: vacancyId,
        })),
      );
      if (error) throw new Error(error.message ?? 'Unable to save documents');
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: vacancyBuilderKeys.documents(vars.vacancyId) });
    },
  });
}

export function useSetVacancyTests(client?: SupabaseClientLike | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      tenantId,
      tests,
      vacancyId,
    }: {
      vacancyId: string;
      tenantId: string;
      tests: Pick<VacancyTest, 'test_key' | 'display_order' | 'candidate_can_take_now'>[];
    }): Promise<void> => {
      const supabase = db(client);
      const { error: deleteError } = await supabase
        .from('vacancy_tests')
        .delete()
        .eq('vacancy_id', vacancyId);
      if (deleteError) throw new Error(deleteError.message ?? 'Unable to reset assessments');
      if (tests.length === 0) return;
      const { error } = await supabase.from('vacancy_tests').insert(
        tests.map((test) => ({
          ...test,
          tenant_id: tenantId,
          vacancy_id: vacancyId,
        })),
      );
      if (error) throw new Error(error.message ?? 'Unable to save assessments');
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: vacancyBuilderKeys.tests(vars.vacancyId) });
    },
  });
}

export function useCreateVacancyPosting(client?: SupabaseClientLike | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateJobPostingInput): Promise<JobPosting> => {
      const supabase = db(client);
      const tenantId = await currentTenantId(supabase);
      const { data, error } = await supabase
        .from('job_postings')
        .insert({
          ...input,
          tenant_id: tenantId,
          visibility:
            input.status === 'published' || input.status === 'open' ? 'public' : 'internal',
        })
        .select('*')
        .single();
      if (error) throw new Error(error.message ?? 'Unable to create vacancy');
      return data as JobPosting;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: vacancyBuilderKeys.all });
    },
  });
}

export function useUpdateVacancyPosting(client?: SupabaseClientLike | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      ...input
    }: { id: string } & UpdateJobPostingInput): Promise<JobPosting> => {
      const { data, error } = await db(client)
        .from('job_postings')
        .update({
          ...input,
          visibility:
            input.status === 'published' || input.status === 'open' ? 'public' : undefined,
        })
        .eq('id', id)
        .select('*')
        .single();
      if (error) throw new Error(error.message ?? 'Unable to update vacancy');
      return data as JobPosting;
    },
    onSuccess: (posting) => {
      void qc.invalidateQueries({ queryKey: vacancyBuilderKeys.posting(posting.id) });
      void qc.invalidateQueries({ queryKey: vacancyBuilderKeys.all });
    },
  });
}

export function usePublicVacancy(uid: string, client?: SupabaseClientLike | null) {
  return useQuery({
    queryKey: vacancyBuilderKeys.publicVacancy(uid),
    enabled: Boolean(uid),
    queryFn: async (): Promise<PublicVacancy | null> => {
      const { data, error } = await db(client)
        .from('job_postings')
        .select(
          `
          *,
          hr_company_profiles:company_profile_id (
            name,
            logo_storage_path,
            brand_color,
            industry,
            company_size,
            website,
            description
          ),
          vacancy_documents (*),
          vacancy_tests (*)
        `,
        )
        .eq('uid', uid)
        .eq('visibility', 'public')
        .in('status', ['open', 'published'])
        .maybeSingle();
      if (error) throw new Error(error.message ?? 'Unable to load public vacancy');
      if (!data) return null;
      const row = data as Record<string, unknown> & {
        hr_company_profiles?: PublicVacancy['company'] | null;
        vacancy_documents?: VacancyDocument[];
        vacancy_tests?: VacancyTest[];
      };
      return {
        ...(row as unknown as Omit<PublicVacancy, 'company' | 'documents' | 'tests'>),
        company: row.hr_company_profiles ?? {
          brand_color: 'var(--color-brand)',
          company_size: null,
          description: null,
          industry: null,
          logo_storage_path: null,
          name: '',
          website: null,
        },
        documents: row.vacancy_documents ?? [],
        tests: row.vacancy_tests ?? [],
      };
    },
  });
}

export function useSubmitPublicApplication(client?: SupabaseClientLike | null) {
  return useMutation({
    mutationFn: async (input: PublicApplicationInput): Promise<PublicApplicationResult> => {
      const supabase = db(client);
      if (!supabase.rpc) throw new Error('Supabase RPC client is required');
      const { data, error } = await supabase
        .rpc('create_public_application', {
          p_consent: input.consent,
          p_email: input.email,
          p_full_name: input.fullName,
          p_linkedin_url: input.linkedinUrl ?? null,
          p_phone: input.phone ?? null,
          p_vacancy_id: input.vacancyId,
        })
        .single();
      if (error) throw new Error(error.message ?? 'Unable to submit application');
      const auth = supabase.auth as typeof supabase.auth & {
        signInWithOtp?: (args: { email: string }) => Promise<unknown>;
      };
      await auth.signInWithOtp?.({ email: input.email }).catch(() => undefined);
      return data as PublicApplicationResult;
    },
  });
}
